import { L, tr, trField } from "./i18n";

export const STANDARD_ALIASES = {
  code: ["Unit", "Unit code"], status: ["Status"], type: ["Type"],
  floor: ["Floor", "floor_no"],
  net_area: ["Net Area", "net m²", "Net m²", "area", "Area", "Area (sqm)", "Area (m²)", "area_sqm", "area_m2", "net_m2"],
  gross_area: ["Gross Area", "gross m²", "Gross m²", "gross_m2"],
  view: ["View"], price: ["Price", "Price (USD)", "Price (EUR)", "price_usd", "Price USD"],
  currency: ["Currency", "ccy"], bedrooms: ["Bedrooms", "Beds"],
};
const present = (v) => v != null && v !== "";
export function definitionForKey(key, definitions = []) {
  const needle = String(key).toLowerCase();
  return definitions.find(fd => [fd.key, fd.label, ...(fd.aliases || [])].some(k => String(k || "").toLowerCase() === needle));
}
export function standardDefinition(key) {
  return { key, _fallback: true, canonical_source: "top_level", label: key, aliases: STANDARD_ALIASES[key] || [], unit: ["net_area", "gross_area"].includes(key) ? "m²" : undefined, data_type: key === "price" ? "currency" : undefined };
}
/** Return both raw value and the actual source key, so display translation
 * does not alter the raw values used for filtering and sorting. */
export function resolveUnitField(entity, fd) {
  if (!entity || !fd) return { key: fd?.key, value: null, topLevel: false };
  if (fd.canonical_source === "top_level" && present(entity[fd.key])) return { key: fd.key, value: entity[fd.key], topLevel: true };
  const bag = entity.fields || {};
  const candidates = [...(fd.aliases || []), fd.label, fd.key].filter(Boolean);
  for (const key of candidates) if (present(bag[key])) return { key, value: bag[key], topLevel: false };
  for (const candidate of candidates) {
    const key = Object.keys(bag).find(k => k.toLowerCase() === String(candidate).toLowerCase());
    if (key && present(bag[key])) return { key, value: bag[key], topLevel: false };
  }
  for (const key of [fd.key, ...(fd.aliases || [])]) if (present(entity[key])) return { key, value: entity[key], topLevel: true };
  return { key: fd.key, value: null, topLevel: true };
}
export function resolveUnitValue(entity, fd) { return resolveUnitField(entity, fd).value; }
export function resolveDisplayValue(entity, fd, lang, defaultLang) {
  const source = resolveUnitField(entity, fd);
  const value = source.topLevel ? tr(entity, source.key, lang, defaultLang) : trField(entity, source.key, lang, defaultLang);
  if (fd?.key === "status") return L(lang, source.value) || source.value;
  if (fd?.key === "type") return L(lang, value ?? source.value) || value || source.value;
  return value ?? source.value;
}
export function formatFieldValue(entity, fd, lang, defaultLang, project = {}) {
  const value = resolveDisplayValue(entity, fd, lang, defaultLang);
  if (value == null || value === "") return "";
  if (fd.data_type === "currency" || fd.key === "price") {
    const number = toNumberOrNull(resolveUnitValue(entity, fd));
    const currency = fd.currency || resolveUnitValue(entity, standardDefinition("currency")) || project.currency || "USD";
    if (number != null) {
      try { return new Intl.NumberFormat(lang || "en", { style: "currency", currency, maximumFractionDigits: 2 }).format(number); } catch { return `${number} ${currency}`; }
    }
  }
  const display = Array.isArray(value) ? value.join(", ") : String(value);
  return fd.unit ? `${display} ${fd.unit}` : display;
}
/** Explicitly configured flags apply to aliases too; unknown legacy fields
 * remain visible so older projects do not lose their inventory details. */
export function visibleUnitFields(entity, definitions = [], surface = "detail") {
  const rows = [], seen = new Set();
  const add = (fd, source) => {
    const configured = definitions.find(other => !other._fallback && other !== fd && [other.key, other.label, ...(other.aliases || [])].some(key => String(key || "").toLowerCase() === String(source.key || "").toLowerCase()));
    if (fd._fallback && configured) return;
    if (!present(source.value) || fd.public_visible === false || fd[`${surface}_visible`] === false) return;
    if (seen.has(fd.key)) return;
    seen.add(fd.key); rows.push({ key: fd.key, sourceKey: source.key, value: source.value, fd: { ...fd, aliases: [...new Set([source.key, ...(fd.aliases || [])])] } });
  };
  for (const fd of definitions) add(fd, resolveUnitField(entity, fd));
  for (const key of Object.keys(entity?.fields || {})) {
    const fd = definitionForKey(key, definitions);
    if (fd) { add(fd, resolveUnitField(entity, fd)); continue; }
    const canonical = Object.keys(STANDARD_ALIASES).find(k => [k, ...STANDARD_ALIASES[k]].some(a => a.toLowerCase() === key.toLowerCase()));
    const fallback = canonical ? standardDefinition(canonical) : { key, label: key };
    // Respect a configured canonical definition even when a legacy alias wasn't listed.
    const configured = canonical && definitions.find(f => f.key === canonical);
    add(configured || fallback, configured ? resolveUnitField(entity, { ...configured, aliases: [...(configured.aliases || []), key] }) : resolveUnitField(entity, fallback));
  }
  return rows;
}
/** Accept machine numbers and common English / Turkish decimal formats. */
export function toNumberOrNull(value) {
  if (value == null || value === "") return null;
  if (typeof value === "number") return Number.isFinite(value) ? value : null;
  let s = String(value).trim().replace(/[\s\u00a0\u202f']/g, "");
  if (!/^[+-]?[\d.,]+$/.test(s)) return null;
  const comma = s.lastIndexOf(","), dot = s.lastIndexOf(".");
  if (comma >= 0 && dot >= 0) {
    const decimal = comma > dot ? "," : ".";
    s = s.replace(decimal === "," ? /\./g : /,/g, "");
    if (decimal === ",") s = s.replace(",", ".");
  } else if (comma >= 0 || dot >= 0) {
    const sep = comma >= 0 ? "," : ".";
    const parts = s.split(sep);
    const grouped = parts.length > 2 ? parts.slice(1).every(p => p.length === 3) : parts[1].length === 3 && !/^[+-]?0$/.test(parts[0]);
    if (grouped) s = parts.join("");
    else if (parts.length === 2) s = parts.join(".");
    else return null;
  }
  const n = Number(s);
  return Number.isFinite(n) ? n : null;
}
export function isEmptyFilter(value) {
  return value == null || value === "" || (Array.isArray(value) && value.length === 0) ||
    (typeof value === "object" && !Array.isArray(value) && Object.values(value).every(v => v == null || v === ""));
}
export function matchesFilter(unit, fd, filterValue) {
  if (isEmptyFilter(filterValue)) return true;
  const value = resolveUnitValue(unit, fd);
  switch (fd.filter_ui) {
    case "range": {
      const n = toNumberOrNull(value);
      return n != null && (filterValue.min == null || n >= Number(filterValue.min)) && (filterValue.max == null || n <= Number(filterValue.max));
    }
    case "date_range": {
      if (!value) return false;
      const date = Date.parse(String(value));
      const from = filterValue.from ? Date.parse(filterValue.from + "T00:00:00Z") : null;
      const to = filterValue.to ? Date.parse(filterValue.to + "T23:59:59.999Z") : null;
      return Number.isFinite(date) && (from == null || date >= from) && (to == null || date <= to);
    }
    case "multi_select": return Array.isArray(value) ? value.some(v => filterValue.includes(String(v))) : filterValue.includes(String(value));
    case "single_select": return String(value) === String(filterValue);
    case "toggle": return Boolean(filterValue) === (value === true || value === "true" || value === 1 || value === "1" || String(value).toLowerCase() === "yes");
    case "search": return String(value ?? "").toLocaleLowerCase().includes(String(filterValue).trim().toLocaleLowerCase());
    default: return true;
  }
}
export function applyFilters(units, fieldDefs, activeFilters) {
  const byKey = new Map((fieldDefs || []).map(fd => [fd.key, fd]));
  return units.filter(unit => Object.entries(activeFilters || {}).every(([key, value]) => !byKey.has(key) || matchesFilter(unit, byKey.get(key), value)));
}
export function sortUnits(units, fieldDefs = [], sortKey) {
  if (!sortKey || sortKey === "recommended") return units;
  const match = /^(.*)-(asc|desc|natural)$/.exec(sortKey);
  if (!match) return units;
  const key = match[1], direction = match[2] === "desc" ? -1 : 1;
  if (key === "code") return [...units].sort((a, b) => String(a.code || "").localeCompare(String(b.code || ""), undefined, { numeric: true }) * direction);
  if (key === "status") {
    const order = { available: 0, reserved: 1, sold: 2 };
    return [...units].sort((a, b) => ((order[a.status] ?? 9) - (order[b.status] ?? 9)) * direction);
  }
  const keys = key === "area" ? ["net_area", "area_sqm", "area", "gross_area"] : [key];
  const fd = keys.map(k => fieldDefs.find(f => f.key === k)).find(Boolean) || standardDefinition(key === "area" ? "net_area" : key);
  return [...units].sort((a, b) => {
    const av = toNumberOrNull(resolveUnitValue(a, fd)), bv = toNumberOrNull(resolveUnitValue(b, fd));
    if (av == null) return bv == null ? 0 : 1;
    if (bv == null) return -1;
    return (av - bv) * direction;
  });
}
export function fieldLabel(fd, lang, defaultLang) {
  if (!fd) return "";
  const translations = fd.translations || {};
  if (translations[lang] || translations[defaultLang]) return translations[lang] || translations[defaultLang];
  const key = fd.key === "area_sqm" ? "area" : fd.key === "bedrooms" ? "beds" : fd.key;
  const known = Object.hasOwn(STANDARD_ALIASES, fd.key) || ["area", "area_sqm"].includes(fd.key);
  return known ? L(lang, key) : fd.label || fd.key;
}
